import { INSTANCE } from './endpoint';
import { ResultResponse } from '../types/generics';

const getToken = () => `Bearer ${localStorage.getItem('token')}`;

// ========== Notification Preferences API ==========

export interface INotificationPreference {
  _id?: string;
  userId: string;
  emailEnabled: boolean;
  pushEnabled: boolean;
  smsEnabled: boolean;
  globalFrequency: 'immediate' | 'daily' | 'off';
  quietHours?: {
    start: string;
    end: string;
    timezone: string;
  };
  digests: {
    community: 'immediate' | 'daily' | 'off';
    channel: 'immediate' | 'daily' | 'off';
    mentions: 'immediate' | 'daily' | 'off';
    posts: 'immediate' | 'daily' | 'off';
  };
  unsubscribeToken: string;
  createdAt?: Date;
  updatedAt?: Date;
}

export interface IUserChannelSubscription {
  _id?: string;
  userId: string;
  communityIdentifier: string;
  channelIdentifier: string;
  frequency?: 'immediate' | 'daily' | 'off';
  emailEnabled?: boolean;
  pushEnabled?: boolean;
  mutedUntil?: Date;
  createdAt?: Date;
  updatedAt?: Date;
}

export interface IUserPostSubscription {
  _id?: string;
  userId: string;
  postId: string;
  frequency?: 'immediate' | 'daily' | 'off';
  emailEnabled?: boolean;
  pushEnabled?: boolean;
  digestInclusion?: 'include' | 'exclude';
  mutedUntil?: Date;
  createdAt?: Date;
  updatedAt?: Date;
}

export interface IDigestSchedule {
  sendTime?: string; // HH:MM format (24-hour)
  timezone?: string; // IANA timezone
  intervalDays?: number; // 1-7 days
}

export interface ICommunityNotificationPolicy {
  _id?: string;
  communityIdentifier: string;
  policyMode: 'recommended' | 'enforced';
  defaults: {
    globalFrequency?: 'immediate' | 'daily' | 'off';
    emailEnabled?: boolean;
    pushEnabled?: boolean;
  };
  digestSchedule?: IDigestSchedule;
  emergencyEnabled: boolean;
  emergencyActors: string[];
  createdAt?: Date;
  updatedAt?: Date;
}

export interface IChannelNotificationPolicy {
  _id?: string;
  communityIdentifier: string;
  channelIdentifier: string;
  policyMode: 'recommended' | 'enforced';
  defaults: {
    frequency?: 'immediate' | 'daily' | 'off';
    emailEnabled?: boolean;
    pushEnabled?: boolean;
  };
  digestSchedule?: IDigestSchedule;
  createdAt?: Date;
  updatedAt?: Date;
}

// ========== User Preferences ==========

export const getNotificationPreferencesApi = (): Promise<ResultResponse<INotificationPreference>> =>
  new Promise((resolve, reject) => {
    INSTANCE({
      method: 'GET',
      url: '/v1/notification-settings/preferences',
      headers: {
        Authorization: getToken(),
      },
    })
      .then((res) => resolve(res.data))
      .catch((error) => reject(error));
  });

export const updateNotificationPreferencesApi = (data: {
  emailEnabled?: boolean;
  pushEnabled?: boolean;
  globalFrequency?: 'immediate' | 'daily' | 'off';
}): Promise<ResultResponse<INotificationPreference>> =>
  new Promise((resolve, reject) => {
    INSTANCE({
      method: 'PUT',
      url: '/v1/notification-settings/preferences',
      data,
      headers: {
        Authorization: getToken(),
      },
    })
      .then((res) => resolve(res.data))
      .catch((error) => reject(error));
  });

export const updateQuietHoursApi = (data: {
  start: string;
  end: string;
  timezone: string;
}): Promise<ResultResponse<INotificationPreference>> =>
  new Promise((resolve, reject) => {
    INSTANCE({
      method: 'PUT',
      url: '/v1/notification-settings/preferences/quiet-hours',
      data,
      headers: {
        Authorization: getToken(),
      },
    })
      .then((res) => resolve(res.data))
      .catch((error) => reject(error));
  });

export const updateDigestPreferencesApi = (data: {
  community?: 'immediate' | 'daily' | 'off';
  channel?: 'immediate' | 'daily' | 'off';
  mentions?: 'immediate' | 'daily' | 'off';
  posts?: 'immediate' | 'daily' | 'off';
}): Promise<ResultResponse<INotificationPreference>> =>
  new Promise((resolve, reject) => {
    INSTANCE({
      method: 'PUT',
      url: '/v1/notification-settings/preferences/digests',
      data,
      headers: {
        Authorization: getToken(),
      },
    })
      .then((res) => resolve(res.data))
      .catch((error) => reject(error));
  });

// ========== Channel Subscriptions ==========

export const getChannelSubscriptionsApi = (communityIdentifier?: string): Promise<ResultResponse<IUserChannelSubscription[]>> =>
  new Promise((resolve, reject) => {
    INSTANCE({
      method: 'GET',
      url: '/v1/notification-settings/subscriptions/channels',
      params: communityIdentifier ? { communityIdentifier } : {},
      headers: {
        Authorization: getToken(),
      },
    })
      .then((res) => resolve(res.data))
      .catch((error) => reject(error));
  });

export const subscribeToChannelApi = (
  communityIdentifier: string,
  channelIdentifier: string,
  data: {
    frequency?: 'immediate' | 'daily' | 'off';
    emailEnabled?: boolean;
    pushEnabled?: boolean;
  }
): Promise<ResultResponse<IUserChannelSubscription>> =>
  new Promise((resolve, reject) => {
    INSTANCE({
      method: 'PUT',
      url: `/v1/notification-settings/subscriptions/channels/${communityIdentifier}/${channelIdentifier}`,
      data,
      headers: {
        Authorization: getToken(),
      },
    })
      .then((res) => resolve(res.data))
      .catch((error) => reject(error));
  });

export const unsubscribeFromChannelApi = (
  communityIdentifier: string,
  channelIdentifier: string
): Promise<ResultResponse<null>> =>
  new Promise((resolve, reject) => {
    INSTANCE({
      method: 'DELETE',
      url: `/v1/notification-settings/subscriptions/channels/${communityIdentifier}/${channelIdentifier}`,
      headers: {
        Authorization: getToken(),
      },
    })
      .then((res) => resolve(res.data))
      .catch((error) => reject(error));
  });

export const muteChannelApi = (
  communityIdentifier: string,
  channelIdentifier: string,
  muteDurationHours: number = 24
): Promise<ResultResponse<IUserChannelSubscription>> =>
  new Promise((resolve, reject) => {
    INSTANCE({
      method: 'POST',
      url: `/v1/notification-settings/subscriptions/channels/${communityIdentifier}/${channelIdentifier}/mute`,
      data: { muteDurationHours },
      headers: {
        Authorization: getToken(),
      },
    })
      .then((res) => resolve(res.data))
      .catch((error) => reject(error));
  });

export const unmuteChannelApi = (
  communityIdentifier: string,
  channelIdentifier: string
): Promise<ResultResponse<IUserChannelSubscription>> =>
  new Promise((resolve, reject) => {
    INSTANCE({
      method: 'DELETE',
      url: `/v1/notification-settings/subscriptions/channels/${communityIdentifier}/${channelIdentifier}/mute`,
      headers: {
        Authorization: getToken(),
      },
    })
      .then((res) => resolve(res.data))
      .catch((error) => reject(error));
  });

// ========== Post Subscriptions ==========

export const getPostSubscriptionsApi = (postId?: string): Promise<ResultResponse<IUserPostSubscription[]>> =>
  new Promise((resolve, reject) => {
    INSTANCE({
      method: 'GET',
      url: '/v1/notification-settings/subscriptions/posts',
      params: postId ? { postId } : {},
      headers: {
        Authorization: getToken(),
      },
    })
      .then((res) => resolve(res.data))
      .catch((error) => reject(error));
  });

export const subscribeToPostApi = (
  postId: string,
  data: {
    frequency?: 'immediate' | 'daily' | 'off';
    emailEnabled?: boolean;
    pushEnabled?: boolean;
    digestInclusion?: 'include' | 'exclude';
  }
): Promise<ResultResponse<IUserPostSubscription>> =>
  new Promise((resolve, reject) => {
    INSTANCE({
      method: 'PUT',
      url: `/v1/notification-settings/subscriptions/posts/${postId}`,
      data,
      headers: {
        Authorization: getToken(),
      },
    })
      .then((res) => resolve(res.data))
      .catch((error) => reject(error));
  });

export const unsubscribeFromPostApi = (postId: string): Promise<ResultResponse<null>> =>
  new Promise((resolve, reject) => {
    INSTANCE({
      method: 'DELETE',
      url: `/v1/notification-settings/subscriptions/posts/${postId}`,
      headers: {
        Authorization: getToken(),
      },
    })
      .then((res) => resolve(res.data))
      .catch((error) => reject(error));
  });

export const mutePostApi = (
  postId: string,
  muteDurationHours: number = 24
): Promise<ResultResponse<IUserPostSubscription>> =>
  new Promise((resolve, reject) => {
    INSTANCE({
      method: 'POST',
      url: `/v1/notification-settings/subscriptions/posts/${postId}/mute`,
      data: { muteDurationHours },
      headers: {
        Authorization: getToken(),
      },
    })
      .then((res) => resolve(res.data))
      .catch((error) => reject(error));
  });

export const unmutePostApi = (postId: string): Promise<ResultResponse<IUserPostSubscription>> =>
  new Promise((resolve, reject) => {
    INSTANCE({
      method: 'DELETE',
      url: `/v1/notification-settings/subscriptions/posts/${postId}/mute`,
      headers: {
        Authorization: getToken(),
      },
    })
      .then((res) => resolve(res.data))
      .catch((error) => reject(error));
  });

// ========== Admin: Community Policies ==========

export const getCommunityPolicyApi = (communityIdentifier: string): Promise<ResultResponse<ICommunityNotificationPolicy>> =>
  new Promise((resolve, reject) => {
    INSTANCE({
      method: 'GET',
      url: `/v1/notification-settings/policies/community/${communityIdentifier}`,
      headers: {
        Authorization: getToken(),
      },
    })
      .then((res) => resolve(res.data))
      .catch((error) => reject(error));
  });

export const updateCommunityPolicyApi = (
  communityIdentifier: string,
  data: {
    policyMode?: 'recommended' | 'enforced';
    defaults?: {
      globalFrequency?: 'immediate' | 'daily' | 'off';
      emailEnabled?: boolean;
      pushEnabled?: boolean;
    };
    digestSchedule?: IDigestSchedule;
    emergencyEnabled?: boolean;
    emergencyActors?: string[];
  }
): Promise<ResultResponse<ICommunityNotificationPolicy>> =>
  new Promise((resolve, reject) => {
    INSTANCE({
      method: 'PUT',
      url: `/v1/notification-settings/policies/community/${communityIdentifier}`,
      data,
      headers: {
        Authorization: getToken(),
      },
    })
      .then((res) => resolve(res.data))
      .catch((error) => reject(error));
  });

// ========== Admin: Channel Policies ==========

export const getChannelPolicyApi = (
  communityIdentifier: string,
  channelIdentifier: string
): Promise<ResultResponse<IChannelNotificationPolicy>> =>
  new Promise((resolve, reject) => {
    INSTANCE({
      method: 'GET',
      url: `/v1/notification-settings/policies/channel/${communityIdentifier}/${channelIdentifier}`,
      headers: {
        Authorization: getToken(),
      },
    })
      .then((res) => resolve(res.data))
      .catch((error) => reject(error));
  });

export const updateChannelPolicyApi = (
  communityIdentifier: string,
  channelIdentifier: string,
  data: {
    policyMode?: 'recommended' | 'enforced';
    defaults?: {
      frequency?: 'immediate' | 'daily' | 'off';
      emailEnabled?: boolean;
      pushEnabled?: boolean;
    };
    digestSchedule?: IDigestSchedule;
  }
): Promise<ResultResponse<IChannelNotificationPolicy>> =>
  new Promise((resolve, reject) => {
    INSTANCE({
      method: 'PUT',
      url: `/v1/notification-settings/policies/channel/${communityIdentifier}/${channelIdentifier}`,
      data,
      headers: {
        Authorization: getToken(),
      },
    })
      .then((res) => resolve(res.data))
      .catch((error) => reject(error));
  });
